<template>
  <div class="product-grid containerV2" :class="classes">
    <template v-for="product in products" :key="product.id">
      <ProductCardHelper
        v-if="!product.skeleton"
        v-slot="{ primaryMedia, secondaryMedia }"
        :product="product"
        :collection-handle="collection?.handle"
        image-type="ecomImages"
      >
        <ProductCard
          :product="product"
          class="product-card"
          :media="primaryMedia"
          :hover-media="secondaryMedia"
          @productClick="$emit('productClick', $event)"
        />
      </ProductCardHelper>
      <SkeletonProductCard v-else />
    </template>
    <GridPromoTile
      v-for="tile in promoTiles"
      :key="`${tile.link}-${tile.desktopRow}`"
      :title-lines="tile.titleLines"
      :button-text="tile.buttonText"
      :link="tile.link"
      :images="tile.images"
      :desktop-images="tile.desktopImages"
      :style="promoTileStyle(tile)"
    />
    <PromotionBanner
      v-if="promotionBanner"
      :title="promotionBanner.title"
      :button-text="promotionBanner.buttonText"
      :description="promotionBanner.description"
      :vertical-align="promotionBanner.verticalAlign"
      :external="promotionBanner.external"
      class="promotion-banner"
      :class="{
        'promotion-banner--portrait':
          promotionBanner.orientation === 'portrait',
      }"
      :images="promotionBanner.images"
      :orientation="promotionBanner.orientation"
    />
  </div>
</template>

<script>
import ProductCard from "@/components/ProductCard.vue";
import ProductCardHelper from "@/components/product-card/ProductCardHelper.vue";
import SkeletonProductCard from "@/components/skeleton/SkeletonProductCard.vue";
import PromotionBanner from "@/components/collection/PromotionBanner.vue";
import GridPromoTile from "@/components/collection/GridPromoTile.vue";
import { getGridPromoTiles } from "@/helpers/gridPromoTiles";

import { mapState } from "vuex";
import promotionBannerContent from "@/assets/json/collection/PromotionBanner.json";

export default {
  name: "ProductGridWithPromoTiles",
  components: {
    ProductCard,
    ProductCardHelper,
    PromotionBanner,
    SkeletonProductCard,
    GridPromoTile,
  },
  props: {
    narrow: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Array,
      default: () => [],
    },
    showPromoTiles: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["productClick"],
  data() {
    return {
      promotionBannerContent,
    };
  },
  computed: {
    ...mapState("collection", ["collection", "loading"]),
    classes() {
      return {
        "product-grid--narrow": this.narrow,
        "product-grid--with-promo-tiles": this.promoTiles.length > 0,
      };
    },
    promotionBanner() {
      return this.promotionBannerContent[this.collection?.handle];
    },
    promoTiles() {
      if (!this.showPromoTiles) {
        return [];
      }

      return getGridPromoTiles(this.collection?.handle).filter((tile) => {
        if (!tile.minProducts) {
          return true;
        }

        return this.products.length >= tile.minProducts;
      });
    },
  },
  methods: {
    promoTileStyle(tile) {
      return {
        "--promo-row": tile.mobileRow || 4,
        "--promo-row-desktop": tile.desktopRow || 2,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 10px;
  row-gap: 40px;

  @include respond-above(md) {
    column-gap: 20px;
  }

  @include respond-above(lg) {
    grid-template-columns: repeat(4, 1fr);
  }

  &.product-grid--narrow {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 10px;
    row-gap: 40px;
    padding: 0;
  }
}

.product-grid--with-promo-tiles {
  align-items: start;
  container-type: inline-size;

  // Match single product-card image height (4/5 of one column width).
  --product-image-height: calc((100cqw - 10px) / 2 * 5 / 4);

  @include respond-above(md) {
    --product-image-height: calc((100cqw - 20px) / 2 * 5 / 4);
  }

  @include respond-above(lg) {
    --product-image-height: calc((100cqw - 60px) / 4 * 5 / 4);
  }

  :deep(.grid-promo-tile__frame) {
    height: 515px;

    @include respond-above(lg) {
      height: var(--product-image-height);
    }
  }

  :deep(.grid-promo-tile) {
    grid-column: 1 / 3;
    grid-row: var(--promo-row, 4);

    @include respond-above(lg) {
      grid-column: 3 / 5;
      grid-row: var(--promo-row-desktop, 2);
    }
  }
}

.promotion-banner {
  grid-column: 1 / 3;
  grid-row: 2;

  &--portrait {
    grid-column: 1 / 2;
  }
}

.product-card--oversized {
  grid-column-start: span 2;
  grid-row-start: span 2;

  :deep(.media-wrapper) {
    aspect-ratio: auto;
    padding-top: 135%;
    position: relative;

    .media {
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }
}
</style>
